import { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractAxiosError } from "@/lib/api";
import { AdminInlineLoading } from "@/components/admin/AdminPanelShell";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

function formatWhen(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch (_e) {
        return iso;
    }
}

function TrafficChart({ points }) {
    const width = 720;
    const height = 200;
    const padding = { top: 12, right: 8, bottom: 28, left: 8 };
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;
    const max = Math.max(1, ...points.map((p) => p.count || 0));

    const coords = points.map((point, index) => {
        const x = padding.left + (points.length <= 1 ? innerW / 2 : (index / (points.length - 1)) * innerW);
        const y = padding.top + innerH - ((point.count || 0) / max) * innerH;
        return { x, y, ...point };
    });

    const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ");
    const areaPath = coords.length
        ? `${linePath} L ${coords[coords.length - 1].x} ${padding.top + innerH} L ${coords[0].x} ${padding.top + innerH} Z`
        : "";

    const ticks = useMemo(() => {
        if (points.length <= 6) return points.map((p, i) => ({ index: i, label: p.label }));
        const step = Math.ceil(points.length / 6);
        return points.map((p, i) => ({ index: i, label: p.label })).filter((t) => t.index % step === 0 || t.index === points.length - 1);
    }, [points]);

    return (
        <div className="overflow-x-auto">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full min-w-[320px]" role="img" aria-label="Requests over time">
                <defs>
                    <linearGradient id="trafficArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5d00ff" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="#5d00ff" stopOpacity="0.02" />
                    </linearGradient>
                </defs>
                {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                    const y = padding.top + innerH * (1 - ratio);
                    return (
                        <line key={ratio} x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
                    );
                })}
                {areaPath && <path d={areaPath} fill="url(#trafficArea)" />}
                {linePath && <path d={linePath} fill="none" stroke="#5d00ff" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />}
                {coords.map((c) => (
                    <circle key={c.hour} cx={c.x} cy={c.y} r={c.count > 0 ? 3 : 2} fill={c.count > 0 ? "#5d00ff" : "rgba(255,255,255,0.15)"} />
                ))}
                {ticks.map((tick) => {
                    const c = coords[tick.index];
                    if (!c) return null;
                    return (
                        <text key={tick.index} x={c.x} y={height - 8} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="10">
                            {tick.label}
                        </text>
                    );
                })}
            </svg>
        </div>
    );
}

export function AdminTrafficPage() {
    const [data, setData] = useState(null);
    const [hours, setHours] = useState(24);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/admin/traffic", { params: { hours } });
            setData(payload);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load traffic analytics"));
        } finally {
            setLoading(false);
        }
    }, [hours]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) return <AdminInlineLoading label="Loading traffic analytics…" />;

    const chartPoints = data?.by_hour || [];

    return (
        <div className="space-y-4" data-testid="admin-traffic">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="hidden lg:block">
                    <h2 className="font-heading text-xl tracking-tight">Traffic analytics</h2>
                    <p className="mt-1 text-sm text-white/45">API requests with timing, routes, IP addresses, and geolocation.</p>
                </div>
                <div className="flex w-full items-center justify-end gap-2 lg:w-auto">
                    <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="input-dark rounded-md px-3 py-2 text-sm">
                        <option value={24}>Last 24 hours</option>
                        <option value={72}>Last 3 days</option>
                        <option value={168}>Last 7 days</option>
                    </select>
                    <button type="button" onClick={load} className="btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                    ["Total requests", data?.total_requests ?? 0],
                    ["Unique IPs", data?.unique_ips ?? 0],
                    ["Avg duration", `${data?.avg_duration_ms ?? 0} ms`],
                    ["Window", `${data?.hours ?? hours}h`],
                ].map(([label, value]) => (
                    <div key={label} className="surface p-4">
                        <p className="label-eyebrow">{label}</p>
                        <p className="mt-2 font-mono text-xl tabular-nums">{value}</p>
                    </div>
                ))}
            </div>

            <div className="surface p-5">
                <p className="label-eyebrow">Requests over time</p>
                <div className="mt-4">
                    {chartPoints.length ? (
                        <TrafficChart points={chartPoints} />
                    ) : (
                        <p className="text-sm text-white/40">No traffic recorded yet. Browse the app or admin console to generate API traffic.</p>
                    )}
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="surface p-5">
                    <p className="label-eyebrow">Top routes</p>
                    <div className="mt-3 divide-y divide-white/[0.06]">
                        {(data?.top_paths || []).map((row) => (
                            <div key={row.path} className="flex items-center justify-between gap-3 py-2 text-sm">
                                <span className="truncate font-mono text-white/75">{row.path}</span>
                                <span className="shrink-0 font-mono tabular-nums text-white/45">{row.count}</span>
                            </div>
                        ))}
                        {!data?.top_paths?.length && <p className="py-4 text-sm text-white/40">No routes yet.</p>}
                    </div>
                </div>
                <div className="surface p-5">
                    <p className="label-eyebrow">Countries</p>
                    <div className="mt-3 divide-y divide-white/[0.06]">
                        {(data?.by_country || []).map((row) => (
                            <div key={`${row.country}-${row.country_code}`} className="flex items-center justify-between gap-3 py-2 text-sm">
                                <span className="text-white/75">{row.country}{row.country_code ? ` (${row.country_code})` : ""}</span>
                                <span className="font-mono tabular-nums text-white/45">{row.count}</span>
                            </div>
                        ))}
                        {!data?.by_country?.length && <p className="py-4 text-sm text-white/40">No geolocation data yet.</p>}
                    </div>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="surface p-5">
                    <p className="label-eyebrow">Top IP addresses</p>
                    <div className="mt-3 divide-y divide-white/[0.06]">
                        {(data?.top_ips || []).map((row) => (
                            <div key={`${row.ip}-${row.country}`} className="py-2 text-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <span className="font-mono text-white/80">{row.ip}</span>
                                    <span className="font-mono tabular-nums text-white/45">{row.count}</span>
                                </div>
                                <p className="mt-0.5 text-xs text-white/40">{[row.city, row.country].filter(Boolean).join(", ") || "Unknown location"}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="surface p-5">
                    <p className="label-eyebrow">Status codes & methods</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {(data?.status_breakdown || []).map((row) => (
                            <span key={row.status} className="rounded-md border border-white/10 px-2.5 py-1 text-xs font-mono">
                                {row.status} · {row.count}
                            </span>
                        ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {(data?.methods || []).map((row) => (
                            <span key={row.method} className="rounded-md border border-white/10 px-2.5 py-1 text-xs font-mono uppercase">
                                {row.method} · {row.count}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="surface overflow-hidden">
                <div className="border-b border-white/[0.06] px-5 py-3">
                    <p className="label-eyebrow">Recent requests</p>
                </div>
                <div className="max-h-[420px] overflow-auto">
                    <table className="w-full text-left text-xs">
                        <thead className="sticky top-0 bg-[#262626] text-[10px] uppercase tracking-wide text-white/40">
                            <tr>
                                <th className="px-4 py-2 font-medium">Time</th>
                                <th className="px-4 py-2 font-medium">Method</th>
                                <th className="px-4 py-2 font-medium">Path</th>
                                <th className="px-4 py-2 font-medium">Status</th>
                                <th className="px-4 py-2 font-medium">IP</th>
                                <th className="px-4 py-2 font-medium">Location</th>
                                <th className="px-4 py-2 font-medium text-right">Ms</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04]">
                            {(data?.recent_requests || []).map((row, idx) => (
                                <tr key={`${row.timestamp}-${idx}`} className="hover:bg-white/[0.02]">
                                    <td className="px-4 py-2 whitespace-nowrap text-white/55">{formatWhen(row.timestamp)}</td>
                                    <td className="px-4 py-2 font-mono uppercase text-white/70">{row.method}</td>
                                    <td className="px-4 py-2 font-mono text-white/75 max-w-[12rem] truncate">{row.path}</td>
                                    <td className="px-4 py-2 font-mono">{row.status}</td>
                                    <td className="px-4 py-2 font-mono text-white/60">{row.ip}</td>
                                    <td className="px-4 py-2 text-white/50">{[row.city, row.region, row.country].filter(Boolean).join(", ") || "—"}</td>
                                    <td className="px-4 py-2 text-right font-mono tabular-nums text-white/45">{row.duration_ms}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!data?.recent_requests?.length && (
                        <p className="p-6 text-sm text-white/40">No recent requests in this window.</p>
                    )}
                </div>
            </div>
        </div>
    );
}
